import styled from "@emotion/styled";

export const GridContainer = styled.div(() => ({
  border: "1px solid #E8E5F0",
  borderRadius: "8px",
  // overflow stays visible so the persona pill dropdown can render below the
  // TabBar without being clipped. Inner rows don't have fills that escape the
  // rounded corners, so the corner radius still reads cleanly.
  marginTop: "16px",
  marginBottom: "16px",
}));

/* ── Tab bar ── */

export const TabBar = styled.div(() => ({
  display: "flex",
  borderBottom: "1px solid #E8E5F0",
}));

export const Tab = styled.div<{ active?: boolean }>(({ active }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  gap: "4px",
  padding: "12px 16px 10px",
  background: active ? "#fff" : "#F8F7FC",
  border: "none",
  borderBottom: active ? "2px solid #7C5CBF" : "2px solid transparent",
  cursor: "pointer",
  transition: "background 0.15s, border-color 0.15s",
  "&:hover": {
    background: active ? "#fff" : "#F0EDF5",
  },
}));

export const TabToggleRow = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

export const TabLabel = styled.span<{ active?: boolean }>(({ active }) => ({
  fontSize: "13px",
  fontWeight: active ? 600 : 500,
  color: active ? "#2D2943" : "#6B6280",
}));

export const TabDescription = styled.span(() => ({
  fontSize: "11px",
  color: "#9B93B0",
  fontWeight: 400,
}));

export const TabPill = styled.button<{
  variant: "primary" | "inherited" | "custom";
  interactive?: boolean;
}>(({ variant, interactive }) => {
  const styles = {
    primary: { color: "#7C5CBF", background: "#F0EBF8", border: "#D8CCF0" },
    inherited: { color: "#6B6280", background: "#F3F2F6", border: "#D3D0DC" },
    custom: { color: "#B45309", background: "#FEF3C7", border: "#FDE68A" },
  };
  const s = styles[variant];
  return {
    fontSize: "10px",
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: "6px",
    color: s.color,
    backgroundColor: s.background,
    border: `1px solid ${s.border}`,
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
    cursor: interactive ? "pointer" : "default",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": interactive ? { borderColor: s.color } : {},
  };
});

export const PillDivider = styled.span(() => ({
  display: "inline-flex",
  alignItems: "center",
  borderLeft: "1px solid currentColor",
  paddingLeft: "6px",
  marginLeft: "2px",
  opacity: 0.5,
}));

export const PillDropdown = styled.div(() => ({
  position: "absolute" as const,
  top: "calc(100% + 6px)",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "#fff",
  border: "1px solid #E8E5F0",
  borderRadius: "8px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
  padding: "12px 16px",
  minWidth: "220px",
  // High enough to sit above adjacent rows + the group-header bar.
  zIndex: 100,
}));

export const PillDropdownTitle = styled.div(() => ({
  fontSize: "13px",
  fontWeight: 600,
  color: "#2D2943",
  marginBottom: "4px",
}));

export const PillDropdownDesc = styled.div(() => ({
  fontSize: "11px",
  color: "#9B93B0",
  lineHeight: "1.4",
  marginBottom: "10px",
}));

export const PillDropdownAction = styled.button(() => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "6px 0",
  background: "none",
  border: "none",
  borderTop: "1px solid #F0EDF5",
  width: "100%",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: 500,
  color: "#7C5CBF",
  "&:hover": {
    color: "#6B4DAF",
  },
}));

/* ── Group sections ── */

export const GroupSection = styled.div(() => ({
  "&:not(:first-of-type)": {
    borderTop: "1px solid #E8E5F0",
  },
}));

export const GroupHeaderBar = styled.div(() => ({
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  gap: "2px",
  padding: "8px 16px",
  backgroundColor: "#F8F7FC",
  borderBottom: "1px solid #E8E5F0",
}));

export const GroupTitle = styled.span(() => ({
  fontSize: "12px",
  fontWeight: 600,
  color: "#6B6280",
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
}));

export const GroupHelperText = styled.span(() => ({
  fontSize: "11px",
  color: "#9B93B0",
  fontWeight: 400,
}));

/* ── Doc rows ── */

export const DocRow = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 10px 10px 16px",
  borderBottom: "1px solid #F0EDF5",
  minHeight: "48px",
  "&:last-child": {
    borderBottom: "none",
  },
}));

export const DocNameCell = styled.div(() => ({
  display: "flex",
  flexDirection: "column" as const,
  gap: "2px",
  overflow: "hidden",
  minWidth: 0,
  flex: 1,
}));

export const DocName = styled.span(() => ({
  fontSize: "14px",
  fontWeight: 500,
  color: "#2D2943",
}));

export const DocControlCell = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexShrink: 0,
}));

export const DisabledDash = styled.span(() => ({
  fontSize: "14px",
  color: "#D3D0DC",
}));

/* ── Shared number control ── */

export const NumberControl = styled.div<{ preset?: boolean; disabled?: boolean }>(
  ({ preset, disabled }) => {
    const muted = preset || disabled;
    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "2px",
      padding: "4px 8px",
      minWidth: "48px",
      border: "1px solid #D3D0DC",
      borderRadius: "6px",
      background: muted ? "#F3F2F6" : "#fff",
      fontSize: "13px",
      color: muted ? "#6B6280" : "#2D2943",
      fontWeight: 500,
      height: "32px",
      boxSizing: "border-box" as const,
      "&:focus-within": {
        borderColor: muted ? "#D3D0DC" : "#7C5CBF",
      },
    };
  }
);

export const NumberControlLabel = styled.span(() => ({
  color: "#6B6280",
  fontSize: "12px",
  whiteSpace: "nowrap" as const,
}));

export const NumberControlInput = styled.input(() => ({
  width: "16px",
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "center" as const,
  color: "#2D2943",
  padding: 0,
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  MozAppearance: "textfield" as const,
  "&:disabled": {
    color: "#6B6280",
    cursor: "default",
  },
}));

export const NumberControlArrows = styled.div(() => ({
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "center",
  gap: "1px",
  marginLeft: "4px",
}));

export const NumberControlArrowButton = styled.button(() => ({
  width: "14px",
  height: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "none",
  borderRadius: "2px",
  cursor: "pointer",
  padding: 0,
  color: "#6B6280",
  "&:hover": {
    background: "#F0EDF5",
    color: "#2D2943",
  },
  "&:disabled": {
    color: "#D3D0DC",
    cursor: "default",
    "&:hover": {
      background: "none",
    },
  },
}));

export const NumberControlReadOnlyValue = styled.span(() => ({
  fontSize: "13px",
  fontWeight: 600,
  color: "inherit",
  minWidth: "16px",
  textAlign: "center" as const,
}));

export const ConfigureRunwayLink = styled.button(() => ({
  background: "none",
  border: "none",
  padding: 0,
  fontSize: "12px",
  fontWeight: 500,
  color: "#7C5CBF",
  cursor: "pointer",
  textDecoration: "underline",
  whiteSpace: "nowrap" as const,
  "&:hover": {
    color: "#6B4DAF",
  },
  "&:disabled": {
    color: "#D3D0DC",
    cursor: "default",
    textDecoration: "none",
  },
}));

/* ── Badges ── */

export const VerifiedBadge = styled.span(() => ({
  marginLeft: "8px",
  padding: "1px 6px",
  fontSize: "9px",
  fontWeight: 500,
  color: "#085041",
  backgroundColor: "#E1F5EE",
  borderRadius: "4px",
  cursor: "pointer",
}));

export const RunwayBadge = styled.span(() => ({
  marginLeft: "8px",
  padding: "1px 6px",
  fontSize: "9px",
  fontWeight: 500,
  color: "#4A1A87",
  backgroundColor: "#EFE8FB",
  borderRadius: "4px",
  cursor: "pointer",
}));

/* ── Custom doc add/delete ── */

export const AddCustomRow = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 16px",
  borderTop: "1px solid #E8E5F0",
  backgroundColor: "#FAFAFA",
}));

export const AddCustomInput = styled.input(() => ({
  flex: 1,
  padding: "6px 10px",
  fontSize: "13px",
  border: "1px solid #D3D0DC",
  borderRadius: "6px",
  outline: "none",
  color: "#2D2943",
  "&:focus": {
    borderColor: "#7C5CBF",
  },
  "&::placeholder": {
    color: "#9B93B0",
  },
}));

export const AddCustomSubmit = styled.button(() => ({
  padding: "6px 16px",
  fontSize: "12px",
  fontWeight: 600,
  color: "#fff",
  backgroundColor: "#7C5CBF",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#6B4DAF",
  },
  "&:disabled": {
    backgroundColor: "#D3D0DC",
    cursor: "default",
  },
}));

export const DeleteButton = styled.button(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "18px",
  height: "18px",
  marginRight: "6px",
  padding: 0,
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "16px",
  fontWeight: 600,
  color: "#D3D0DC",
  borderRadius: "50%",
  flexShrink: 0,
  "&:hover": {
    color: "#DC3545",
    backgroundColor: "#FEE2E2",
  },
}));
